'use client';

import { useState, useEffect, useCallback } from 'react';
import { useTranslations } from 'next-intl';
import { useRouter } from 'next/navigation';
import { useSignIn, useSignUp } from '@medizzconnect/auth';
import Image from 'next/image';

type AuthMode = 'login' | 'register';
type AuthVariant = 'default' | 'merkliste' | 'mediziner';

interface AuthModalProps {
  mode?: AuthMode;
  variant?: AuthVariant;
  onClose: () => void;
}

/**
 * Universelles Auth-Modal — Split-Screen mit Bild links, Formular rechts.
 * Layout aus V1 übernommen.
 * Varianten:
 * - default: Generisches Login/Register
 * - merkliste: Login für Merkliste-Zugriff (Patient/Client)
 * - mediziner: Login für Ärzte mit Register-Link zu /onboarding
 */
export function AuthModal({ mode: initialMode = 'login', variant = 'default', onClose }: AuthModalProps) {
  const t = useTranslations('authModal');
  const tAuth = useTranslations('auth');
  const router = useRouter();
  const [mode, setMode] = useState<AuthMode>(initialMode);

  // Escape zum Schließen + Body scroll lock
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  const handleDoctorRegister = useCallback(() => {
    onClose();
    router.push('/onboarding');
  }, [onClose, router]);

  const handleSuccess = useCallback(async () => {
    onClose();
    // Rolle aus DB laden für rollenbasiertes Redirect
    try {
      const res = await fetch('/api/profile');
      const data = await res.json();
      const role = data?.user?.role_type as string | undefined;

      if (role === 'admin' || role === 'superadmin') {
        router.push('/admin');
      } else if (role === 'client') {
        router.push('/dashboard/favorites');
      } else {
        // solo_doctor, clinic_admin, clinic_member
        router.push('/dashboard');
      }
    } catch {
      // Fallback: Dashboard
      router.push('/dashboard');
    }
  }, [onClose, router]);

  // Titel + Untertitel basierend auf Variante
  const getTitle = () => {
    if (variant === 'merkliste') return t('merklisteTitle');
    if (variant === 'mediziner') return mode === 'login' ? t('doctorLoginTitle') : t('doctorRegisterTitle');
    return mode === 'login' ? t('loginTitle') : t('registerTitle');
  };

  const getSubtitle = () => {
    if (variant === 'merkliste') return t('merklisteSubtitle');
    if (variant === 'mediziner') return mode === 'login' ? t('doctorLoginSubtitle') : t('doctorRegisterSubtitle');
    return mode === 'login' ? t('loginSubtitle') : t('registerSubtitle');
  };

  // Spotlight (linke Seite) Inhalte
  const getSpotlight = () => {
    if (variant === 'merkliste') {
      return {
        title: t('merklisteSpotlightTitle'),
        subtitle: t('merklisteSpotlightSubtitle'),
        image: '/images/merkliste.jpg',
        features: [
          { icon: '✓', title: t('merklisteFeature1') },
          { icon: '✓', title: t('merklisteFeature2') },
          { icon: '✓', title: t('merklisteFeature3') },
        ],
      };
    }
    if (variant === 'mediziner') {
      return {
        title: t('medizinerSpotlightTitle'),
        subtitle: t('medizinerSpotlightSubtitle'),
        image: '/images/medizineranmelden.jpg',
        features: [
          { icon: '✓', title: t('medizinerFeature1') },
          { icon: '✓', title: t('medizinerFeature2') },
          { icon: '✓', title: t('medizinerFeature3') },
        ],
      };
    }
    return {
      title: t('defaultSpotlightTitle'),
      subtitle: t('defaultSpotlightSubtitle'),
      image: '/images/merkliste.jpg',
      features: [
        { icon: '👩‍⚕️', title: t('defaultFeature1'), desc: t('defaultFeature1Desc') },
        { icon: '📋', title: t('defaultFeature2'), desc: t('defaultFeature2Desc') },
        { icon: '🔒', title: t('defaultFeature3'), desc: t('defaultFeature3Desc') },
      ],
    };
  };

  const spotlight = getSpotlight();

  return (
    <div
      className="fixed inset-0 z-[3000] flex items-center justify-center bg-slate-900/45 backdrop-blur-[16px] backdrop-saturate-[160%] p-4 md:p-6"
    >
      <div
        className="relative flex w-full max-w-[980px] min-h-[560px] flex-col md:flex-row rounded-[28px] bg-white shadow-[0_24px_48px_rgba(15,23,42,0.18),0_12px_24px_rgba(15,23,42,0.12)] overflow-hidden animate-in fade-in zoom-in-95 duration-200"
        role="dialog"
        aria-modal="true"
        aria-label={getTitle()}
      >
        {/* Schließen-Button */}
        <button
          onClick={onClose}
          className="absolute top-[18px] right-[18px] z-10 flex h-10 w-10 items-center justify-center rounded-full bg-white/85 text-slate-400 transition hover:scale-105 hover:shadow-lg"
          aria-label={t('close')}
        >
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* ─── Linke Seite — Bild + Spotlight ─── */}
        <div className="relative flex-1 flex items-end justify-start min-h-[220px] md:min-h-0 overflow-hidden">
          {/* Hintergrundbild */}
          <Image
            src={spotlight.image}
            alt=""
            fill
            className="object-cover"
            priority
          />
          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-br from-slate-900/75 to-teal-700/85" />

          {/* Content über dem Bild */}
          <div className="relative z-[1] p-7 md:p-10 lg:p-12 max-w-[340px] grid gap-5 md:gap-6 text-white">
            <h1 className="text-[26px] md:text-[32px] font-bold leading-[1.1] tracking-tight m-0">
              {spotlight.title}
            </h1>
            <p className="text-[15px] leading-relaxed text-white/85 m-0">
              {spotlight.subtitle}
            </p>
            <div className="grid gap-4">
              {spotlight.features.map((feature, i) => (
                <div key={i} className="grid grid-cols-[auto_1fr] items-center gap-3">
                  <div className="w-10 h-10 rounded-2xl bg-white/15 grid place-items-center text-lg">
                    {feature.icon}
                  </div>
                  <div>
                    <strong className="block text-[15px] font-semibold">{feature.title}</strong>
                    {'desc' in feature && feature.desc && (
                      <p className="mt-1 text-[13px] text-white/70 m-0">{feature.desc}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ─── Rechte Seite — Formular ─── */}
        <div className="flex-1 flex items-center justify-center bg-white p-7 md:p-10 lg:p-12">
          <div className="w-full max-w-[360px] grid gap-5">
            {/* Header */}
            <div>
              <h2 className="text-[26px] font-semibold text-slate-900 m-0">{getTitle()}</h2>
              <p className="mt-1 text-sm text-slate-500 m-0">{getSubtitle()}</p>
            </div>

            {/* Custom Forms — kein Clerk UI */}
            {mode === 'login' ? (
              <LoginForm onSuccess={handleSuccess} />
            ) : (
              <RegisterForm onSuccess={handleSuccess} />
            )}

            {/* Footer / Toggle */}
            <div className="text-center pt-2">
              {variant === 'mediziner' ? (
                mode === 'login' ? (
                  <p className="text-sm text-slate-500">
                    {t('noAccountYet')}{' '}
                    <button
                      onClick={handleDoctorRegister}
                      className="font-medium text-teal-700 underline underline-offset-4 hover:text-teal-600 transition-colors"
                    >
                      {t('registerAsDoctorLink')}
                    </button>
                  </p>
                ) : null
              ) : (
                mode === 'login' ? (
                  <p className="text-sm text-slate-500">
                    {tAuth('noAccount')}{' '}
                    <button
                      onClick={() => setMode('register')}
                      className="font-medium text-teal-700 underline underline-offset-4 hover:text-teal-600 transition-colors"
                    >
                      {tAuth('signUp')}
                    </button>
                  </p>
                ) : (
                  <p className="text-sm text-slate-500">
                    {tAuth('alreadyAccount')}{' '}
                    <button
                      onClick={() => setMode('login')}
                      className="font-medium text-teal-700 underline underline-offset-4 hover:text-teal-600 transition-colors"
                    >
                      {tAuth('signIn')}
                    </button>
                  </p>
                )
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Custom Login Form (kein Clerk UI, V1-Style) ─── */
function LoginForm({ onSuccess }: { onSuccess: () => void }) {
  const tAuth = useTranslations('auth');
  const { signIn } = useSignIn();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [mfaStep, setMfaStep] = useState<'login' | 'mfa'>('login');
  const [mfaCode, setMfaCode] = useState('');
  const [mfaStrategy, setMfaStrategy] = useState<'email' | 'phone'>('email');
  // Passwort-Reset-Flow: 'login' | 'forgot-email' | 'forgot-code'
  const [forgotStep, setForgotStep] = useState<'login' | 'forgot-email' | 'forgot-code'>('login');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!signIn) return;

    setLoading(true);
    setError('');

    try {
      // ── Transparente Passwort-Migration: alter Clerk → neuer Clerk ──
      try {
        await fetch('/api/auth/password-migrate', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, password }),
        });
      } catch {
        // Stille Fehler — normaler Login-Versuch folgt trotzdem
      }

      const createResult = await signIn.create({ identifier: email });
      if (createResult.error) {
        const e = createResult.error as { code?: string; message?: string };
        if (e.code === 'form_identifier_not_found') {
          setError(tAuth('loginErrorWithHint'));
        } else {
          setError(e.message || tAuth('loginError'));
        }
        return;
      }

      const passwordResult = await signIn.password({ password });
      if (passwordResult.error) {
        const e = passwordResult.error as { code?: string; message?: string };
        if (e.code === 'form_password_incorrect' || e.code === 'form_identifier_not_found') {
          setError(tAuth('loginErrorWithHint'));
        } else {
          setError(e.message || tAuth('loginError'));
        }
        return;
      }

      if (signIn.status === 'needs_client_trust' || signIn.status === 'needs_second_factor') {
        try {
          const supportsEmail = signIn.supportedSecondFactors?.some(
            (factor: { strategy?: string }) => factor?.strategy === 'email_code'
          );
          if (supportsEmail) {
            await signIn.mfa.sendEmailCode();
            setMfaStrategy('email');
          } else {
            await signIn.mfa.sendPhoneCode();
            setMfaStrategy('phone');
          }
          setMfaStep('mfa');
          return;
        } catch {
          setError('Zusätzliche Verifikation fehlgeschlagen. Bitte erneut versuchen.');
          return;
        }
      }

      if (signIn.status === 'needs_new_password') {
        setError('Passwort muss geändert werden. Bitte Passwort-Reset nutzen.');
        return;
      }

      if (signIn.status === 'complete') {
        await signIn.finalize();
        onSuccess();
      }
    } catch (err: unknown) {
      const clerkErr = err as { errors?: { code?: string; message?: string }[] };
      const errCode = clerkErr.errors?.[0]?.code;
      if (errCode === 'form_password_incorrect' || errCode === 'form_identifier_not_found') {
        setError(tAuth('loginErrorWithHint'));
      } else {
        setError(clerkErr.errors?.[0]?.message || tAuth('loginError'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyMfa = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!signIn) return;
    setLoading(true);
    setError('');
    try {
      const verifyResult = mfaStrategy === 'phone'
        ? await signIn.mfa.verifyPhoneCode({ code: mfaCode })
        : await signIn.mfa.verifyEmailCode({ code: mfaCode });
      if (verifyResult.error) {
        const e = verifyResult.error as { message?: string };
        setError(e.message || 'Code ungültig. Bitte erneut versuchen.');
        return;
      }
      if (signIn.status === 'complete') {
        await signIn.finalize();
        onSuccess();
      }
    } catch (err: unknown) {
      const clerkErr = err as { errors?: { message?: string }[] };
      setError(clerkErr.errors?.[0]?.message || 'Code ungültig. Bitte erneut versuchen.');
    } finally {
      setLoading(false);
    }
  };

  // Schritt 1: Code-E-Mail senden
  const handleForgotSendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!signIn) return;
    setLoading(true);
    setError('');
    try {
      const createResult = await signIn.create({ identifier: email });
      if (createResult.error) {
        const e = createResult.error as { message?: string };
        setError(e.message || tAuth('resetCodeSendError'));
        return;
      }
      const sendResult = await signIn.resetPasswordEmailCode.sendCode();
      if (sendResult.error) {
        const e = sendResult.error as { message?: string };
        setError(e.message || tAuth('resetCodeSendError'));
        return;
      }
      setForgotStep('forgot-code');
    } catch (err: unknown) {
      const clerkErr = err as { errors?: { message?: string }[] };
      setError(clerkErr.errors?.[0]?.message || tAuth('resetCodeSendError'));
    } finally {
      setLoading(false);
    }
  };

  // Schritt 2: Code prüfen + neues Passwort setzen
  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!signIn) return;
    setLoading(true);
    setError('');
    try {
      const verifyResult = await signIn.resetPasswordEmailCode.verifyCode({ code: resetCode });
      if (verifyResult.error) {
        const e = verifyResult.error as { code?: string; message?: string };
        if (e.code === 'form_code_incorrect') {
          setError(tAuth('resetCodeInvalid'));
        } else {
          setError(e.message || tAuth('resetPasswordError'));
        }
        return;
      }

      const submitResult = await signIn.resetPasswordEmailCode.submitPassword({
        password: newPassword,
        signOutOfOtherSessions: true,
      });
      if (submitResult.error) {
        const e = submitResult.error as { message?: string };
        setError(e.message || tAuth('resetPasswordError'));
        return;
      }

      if (signIn.status === 'complete') {
        await signIn.finalize();
        setResetSuccess(true);
        setTimeout(() => onSuccess(), 1500);
      }
    } catch (err: unknown) {
      const clerkErr = err as { errors?: { code?: string; message?: string }[] };
      const errCode = clerkErr.errors?.[0]?.code;
      if (errCode === 'form_code_incorrect') {
        setError(tAuth('resetCodeInvalid'));
      } else {
        setError(clerkErr.errors?.[0]?.message || tAuth('resetPasswordError'));
      }
    } finally {
      setLoading(false);
    }
  };

  // ── Passwort-vergessen: Schritt 2 (Code + neues Passwort) ──
  if (forgotStep === 'forgot-code') {
    if (resetSuccess) {
      return (
        <div className="grid gap-5 text-center">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-teal-700/10">
            <svg className="h-7 w-7 text-teal-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <p className="text-sm font-medium text-gray-800">{tAuth('resetSuccess')}</p>
        </div>
      );
    }
    return (
      <form onSubmit={handleResetPassword} className="grid gap-5">
        <div className="text-center mb-1">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-teal-700/10">
            <svg className="h-6 w-6 text-teal-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 5.25a3 3 0 013 3m3 0a6 6 0 01-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1121.75 8.25z" />
            </svg>
          </div>
          <p className="text-sm text-gray-600">
            {tAuth('resetCodeSentTo')} <strong>{email}</strong>
          </p>
        </div>

        <div className="grid gap-2">
          <label htmlFor="reset-code" className="text-[13px] font-medium text-gray-800">
            {tAuth('resetCode')}
          </label>
          <input
            id="reset-code"
            type="text"
            required
            inputMode="numeric"
            autoComplete="one-time-code"
            value={resetCode}
            onChange={(e) => setResetCode(e.target.value)}
            className="h-11 rounded-xl border border-slate-300 px-3.5 text-[15px] text-center tracking-widest outline-none transition focus:border-teal-700 focus:ring-[3px] focus:ring-teal-400/25"
            placeholder="000000"
          />
        </div>

        <div className="grid gap-2">
          <label htmlFor="new-password" className="text-[13px] font-medium text-gray-800">
            {tAuth('newPassword')}
          </label>
          <div className="relative">
            <input
              id="new-password"
              type={showNewPassword ? 'text' : 'password'}
              required
              minLength={8}
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="h-11 w-full rounded-xl border border-slate-300 px-3.5 pr-11 text-[15px] outline-none transition focus:border-teal-700 focus:ring-[3px] focus:ring-teal-400/25"
              placeholder="••••••••"
            />
            <button
              type="button"
              onClick={() => setShowNewPassword(!showNewPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
              aria-label={showNewPassword ? 'Passwort verbergen' : 'Passwort anzeigen'}
            >
              {showNewPassword ? (
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                </svg>
              ) : (
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              )}
            </button>
          </div>
          <p className="text-xs text-gray-400 mt-0.5">{tAuth('passwordHint')}</p>
        </div>

        {error && (
          <div className="rounded-xl bg-red-500/10 border border-red-500/35 text-red-700 px-3.5 py-3 text-[13px]">{error}</div>
        )}

        <button
          type="submit"
          disabled={loading || !resetCode || !newPassword}
          className="h-[46px] rounded-full border-none bg-gradient-to-br from-teal-700 to-teal-400 text-white text-[15px] font-semibold transition hover:-translate-y-0.5 hover:shadow-[0_12px_24px_rgba(20,184,166,0.25)] disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0"
        >
          {loading ? tAuth('resettingPassword') : tAuth('setNewPassword')}
        </button>

        <button
          type="button"
          onClick={() => { setForgotStep('forgot-email'); setError(''); setResetCode(''); setNewPassword(''); }}
          className="text-sm text-teal-700 underline underline-offset-4 hover:text-teal-600 transition-colors text-center"
        >
          {tAuth('resendCode')}
        </button>
      </form>
    );
  }

  // ── Passwort-vergessen: Schritt 1 (E-Mail eingeben) ──
  if (forgotStep === 'forgot-email') {
    return (
      <form onSubmit={handleForgotSendCode} className="grid gap-5">
        <div>
          <h3 className="text-[18px] font-semibold text-slate-900">{tAuth('resetPasswordTitle')}</h3>
          <p className="mt-1 text-sm text-slate-500">{tAuth('resetPasswordSubtitle')}</p>
        </div>

        <div className="grid gap-2">
          <label htmlFor="forgot-email" className="text-[13px] font-medium text-gray-800">
            {tAuth('email')}
          </label>
          <input
            id="forgot-email"
            type="email"
            required
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="h-11 rounded-xl border border-slate-300 px-3.5 text-[15px] outline-none transition focus:border-teal-700 focus:ring-[3px] focus:ring-teal-400/25"
            placeholder={tAuth('emailPlaceholder')}
          />
        </div>

        {error && (
          <div className="rounded-xl bg-red-500/10 border border-red-500/35 text-red-700 px-3.5 py-3 text-[13px]">{error}</div>
        )}

        <button
          type="submit"
          disabled={loading || !email}
          className="h-[46px] rounded-full border-none bg-gradient-to-br from-teal-700 to-teal-400 text-white text-[15px] font-semibold transition hover:-translate-y-0.5 hover:shadow-[0_12px_24px_rgba(20,184,166,0.25)] disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0"
        >
          {loading ? tAuth('sendingCode') : tAuth('sendResetCode')}
        </button>

        <button
          type="button"
          onClick={() => { setForgotStep('login'); setError(''); }}
          className="text-sm text-slate-500 hover:text-slate-700 transition-colors text-center"
        >
          ← {tAuth('backToLogin')}
        </button>
      </form>
    );
  }

  // ── MFA / Client Trust ──
  if (mfaStep === 'mfa') {
    return (
      <form onSubmit={handleVerifyMfa} className="grid gap-5">
        <div className="text-center">
          <p className="text-sm text-gray-600">
            Wir haben dir einen Bestätigungscode gesendet. Bitte gib ihn ein.
          </p>
        </div>

        <div className="grid gap-2">
          <label htmlFor="mfa-code" className="text-[13px] font-medium text-gray-800">
            Bestätigungscode
          </label>
          <input
            id="mfa-code"
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            value={mfaCode}
            onChange={(e) => setMfaCode(e.target.value)}
            className="h-11 rounded-xl border border-slate-300 px-3.5 text-[15px] outline-none transition focus:border-teal-700 focus:ring-[3px] focus:ring-teal-400/25"
            placeholder="123456"
          />
        </div>

        {error && (
          <div className="rounded-xl bg-red-500/10 border border-red-500/35 text-red-700 px-3.5 py-3 text-[13px]">{error}</div>
        )}

        <button
          type="submit"
          disabled={loading || !mfaCode}
          className="h-[46px] rounded-full border-none bg-gradient-to-br from-teal-700 to-teal-400 text-white text-[15px] font-semibold transition hover:-translate-y-0.5 hover:shadow-[0_12px_24px_rgba(20,184,166,0.25)] disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0"
        >
          {loading ? 'Prüfen…' : 'Code bestätigen'}
        </button>

        <button
          type="button"
          onClick={() => { setMfaStep('login'); setError(''); setMfaCode(''); }}
          className="text-sm text-slate-500 hover:text-slate-700 transition-colors text-center"
        >
          ← Zurück zum Login
        </button>
      </form>
    );
  }

  // ── Normaler Login ──
  return (
    <form onSubmit={handleSubmit} className="grid gap-5">
      <div className="grid gap-2">
        <label htmlFor="login-email" className="text-[13px] font-medium text-gray-800">
          {tAuth('email')}
        </label>
        <input
          id="login-email"
          type="email"
          required
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="h-11 rounded-xl border border-slate-300 px-3.5 text-[15px] outline-none transition focus:border-teal-700 focus:ring-[3px] focus:ring-teal-400/25"
          placeholder={tAuth('emailPlaceholder')}
        />
      </div>

      <div className="grid gap-2">
        <label htmlFor="login-password" className="text-[13px] font-medium text-gray-800">
          {tAuth('password')}
        </label>
        <div className="relative">
          <input
            id="login-password"
            type={showPassword ? 'text' : 'password'}
            required
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="h-11 w-full rounded-xl border border-slate-300 px-3.5 pr-11 text-[15px] outline-none transition focus:border-teal-700 focus:ring-[3px] focus:ring-teal-400/25"
            placeholder="••••••••"
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
            aria-label={showPassword ? 'Passwort verbergen' : 'Passwort anzeigen'}
          >
            {showPassword ? (
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
              </svg>
            ) : (
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            )}
          </button>
        </div>
      </div>

      <div className="flex justify-end -mt-1">
        <button
          type="button"
          onClick={() => { setForgotStep('forgot-email'); setError(''); }}
          className="text-xs text-teal-700 hover:text-teal-600 hover:underline underline-offset-4 transition-colors"
        >
          {tAuth('forgotPassword')}
        </button>
      </div>

      {error && (
        <div className="rounded-xl bg-red-500/10 border border-red-500/35 text-red-700 px-3.5 py-3 text-[13px]">
          {error}
          {(error.includes(tAuth('loginErrorWithHint')) || error === tAuth('loginErrorWithHint')) && (
            <button
              type="button"
              onClick={() => { setForgotStep('forgot-email'); setError(''); }}
              className="block mt-1 text-xs text-red-700 font-semibold underline underline-offset-2"
            >
              → {tAuth('forgotPassword')}
            </button>
          )}
        </div>
      )}

      <button
        type="submit"
        disabled={loading}
        className="h-[46px] rounded-full border-none bg-gradient-to-br from-teal-700 to-teal-400 text-white text-[15px] font-semibold transition hover:-translate-y-0.5 hover:shadow-[0_12px_24px_rgba(20,184,166,0.25)] disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0"
      >
        {loading ? tAuth('loggingIn') : tAuth('signIn')}
      </button>
    </form>
  );
}

/* ─── Custom Register Form (kein Clerk UI, V1-Style) ─── */
function RegisterForm({ onSuccess }: { onSuccess: () => void }) {
  const tAuth = useTranslations('auth');
  const { signUp } = useSignUp();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [showVerification, setShowVerification] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);

  const extractError = (err: unknown, fallback: string) => {
    const e = err as { message?: string; longMessage?: string; errors?: { message?: string; longMessage?: string }[] };
    return e?.errors?.[0]?.longMessage || e?.errors?.[0]?.message || e?.longMessage || e?.message || fallback;
  };

  const mapVerifyError = (message: string) => {
    const m = message.toLowerCase();
    if (m.includes('expired') || m.includes('abgelaufen')) return 'Der Code ist abgelaufen. Bitte sende einen neuen Code.';
    if (m.includes('incorrect') || m.includes('invalid') || m.includes('falsch') || m.includes('ungültig')) {
      return 'Der Code ist nicht korrekt. Bitte erneut eingeben oder neuen Code senden.';
    }
    if (m.includes('too many') || m.includes('attempt') || m.includes('rate limit')) {
      return 'Zu viele Versuche. Bitte kurz warten und erneut probieren.';
    }
    return message;
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!signUp) return;
    if (!password || password.length < 8) {
      setError(tAuth('passwordHint'));
      return;
    }

    setLoading(true);
    setError('');

    try {
      const createResult = await signUp.create({ emailAddress: email });
      if (createResult.error) {
        setError(extractError(createResult.error, tAuth('registerError')));
        return;
      }

      // Clerk v7: Passwort ggf. als separater Schritt
      const signUpAny = signUp as unknown as {
        password?: (args: { password: string }) => Promise<{ error?: unknown }>;
      };
      if (typeof signUpAny.password === 'function') {
        const passwordResult = await signUpAny.password({ password });
        if (passwordResult?.error) {
          setError(extractError(passwordResult.error, tAuth('registerError')));
          return;
        }
      }

      const sendResult = await signUp.verifications.sendEmailCode();
      if (sendResult.error) {
        setError(extractError(sendResult.error, tAuth('registerError')));
        return;
      }

      setShowVerification(true);
    } catch (err: unknown) {
      setError(extractError(err, tAuth('registerError')));
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (!signUp || resending || loading) return;
    setResending(true);
    setError('');
    try {
      const sendResult = await signUp.verifications.sendEmailCode();
      if (sendResult.error) {
        setError(extractError(sendResult.error, tAuth('registerError')));
      }
    } catch (err: unknown) {
      setError(extractError(err, tAuth('registerError')));
    } finally {
      setResending(false);
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!signUp) return;
    const normalizedCode = verificationCode.replace(/\D/g, '').trim();
    if (normalizedCode.length < 6) {
      setError(tAuth('verificationError'));
      return;
    }

    setLoading(true);
    setError('');

    try {
      const verifyResult = await signUp.verifications.verifyEmailCode({ code: normalizedCode });
      if (verifyResult.error) {
        const message = extractError(verifyResult.error, tAuth('verificationError'));
        setError(mapVerifyError(message));
        return;
      }

      if (signUp.status === 'complete') {
        await signUp.finalize();

        // User in Supabase DB anlegen (Fallback falls Clerk Webhook nicht kam)
        try {
          await fetch('/api/auth/ensure-user', { method: 'POST' });
        } catch (e) {
          console.error('[auth-modal] ensure-user failed:', e);
        }

        onSuccess();
      } else {
        setError(tAuth('verificationError'));
      }
    } catch (err: unknown) {
      const message = extractError(err, tAuth('verificationError'));
      setError(mapVerifyError(message));
    } finally {
      setLoading(false);
    }
  };

  if (showVerification) {
    return (
      <form onSubmit={handleVerify} className="grid gap-5">
        <div className="text-center mb-1">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-teal-700/10">
            <svg className="h-6 w-6 text-teal-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
            </svg>
          </div>
          <p className="text-sm text-gray-600">
            {tAuth('verificationSent')} <strong>{email}</strong>
          </p>
        </div>

        <div className="grid gap-2">
          <label htmlFor="verification-code" className="text-[13px] font-medium text-gray-800">
            {tAuth('verificationCode')}
          </label>
          <input
            id="verification-code"
            type="text"
            required
            inputMode="numeric"
            autoComplete="one-time-code"
            value={verificationCode}
            onChange={(e) => setVerificationCode(e.target.value)}
            className="h-11 rounded-xl border border-slate-300 px-3.5 text-[15px] text-center tracking-widest outline-none transition focus:border-teal-700 focus:ring-[3px] focus:ring-teal-400/25"
            placeholder="000000"
          />
        </div>

        {error && (
          <div className="rounded-xl bg-red-500/10 border border-red-500/35 text-red-700 px-3.5 py-3 text-[13px]">{error}</div>
        )}

        <button
          type="submit"
          disabled={loading || verificationCode.replace(/\D/g, '').trim().length < 6}
          className="h-[46px] rounded-full border-none bg-gradient-to-br from-teal-700 to-teal-400 text-white text-[15px] font-semibold transition hover:-translate-y-0.5 hover:shadow-[0_12px_24px_rgba(20,184,166,0.25)] disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0"
        >
          {loading ? tAuth('verifying') : tAuth('verify')}
        </button>

        <button
          type="button"
          onClick={handleResendCode}
          disabled={resending || loading}
          className="text-sm text-teal-700 underline underline-offset-4 hover:text-teal-600 transition-colors text-center disabled:text-slate-400 disabled:no-underline"
        >
          {resending ? `${tAuth('sendingCode')}...` : tAuth('resendCode')}
        </button>
      </form>
    );
  }

  return (
    <form onSubmit={handleSignUp} className="grid gap-5">
      <div className="grid gap-2">
        <label htmlFor="register-email" className="text-[13px] font-medium text-gray-800">
          {tAuth('email')}
        </label>
        <input
          id="register-email"
          type="email"
          required
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="h-11 rounded-xl border border-slate-300 px-3.5 text-[15px] outline-none transition focus:border-teal-700 focus:ring-[3px] focus:ring-teal-400/25"
          placeholder={tAuth('emailPlaceholder')}
        />
      </div>

      <div className="grid gap-2">
        <label htmlFor="register-password" className="text-[13px] font-medium text-gray-800">
          {tAuth('password')}
        </label>
        <div className="relative">
          <input
            id="register-password"
            type={showPassword ? 'text' : 'password'}
            required
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="h-11 w-full rounded-xl border border-slate-300 px-3.5 pr-11 text-[15px] outline-none transition focus:border-teal-700 focus:ring-[3px] focus:ring-teal-400/25"
            placeholder="••••••••"
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
            aria-label={showPassword ? 'Passwort verbergen' : 'Passwort anzeigen'}
          >
            {showPassword ? (
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
              </svg>
            ) : (
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            )}
          </button>
        </div>
        <p className="text-xs text-gray-400 mt-0.5">{tAuth('passwordHint')}</p>
      </div>

      {error && (
        <div className="rounded-xl bg-red-500/10 border border-red-500/35 text-red-700 px-3.5 py-3 text-[13px]">{error}</div>
      )}

      <button
        type="submit"
        disabled={loading}
        className="h-[46px] rounded-full border-none bg-gradient-to-br from-teal-700 to-teal-400 text-white text-[15px] font-semibold transition hover:-translate-y-0.5 hover:shadow-[0_12px_24px_rgba(20,184,166,0.25)] disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0"
      >
        {loading ? tAuth('registering') : tAuth('signUp')}
      </button>
    </form>
  );
}
